import { FC, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardExportButton, DashboardSectionHeader, DashboardNarrativePanel } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import {
  useFacebookFollowersReport,
  type FollowersTrendGranularity,
} from "../../../hooks/UseOrganicMediaData";
import OrganicDateRangePicker from "src/components/common/OrganicDateRangePicker/OrganicDateRangePicker";
import { normalizeApiLang } from "src/utils/apiLang";
import FacebookFollowersKpiCards from "./FacebookFollowersKpiCards";
import FacebookNewFollowers from "./FacebookNewFollowers";
import FacebookFollowersByCountry from "./FacebookFollowersByCountry";
import FacebookFollowersByCity from "./FacebookFollowersByCity";
import "../../../styles/FacebookFollowersReport/FacebookFollowersReport.scss";

interface FacebookFollowersReportProps {
  dateFilters: import("../FacebookContentReport").OrganicDateFilters;
  topCountriesCount: number | undefined;
  topCitiesCount: number | undefined;
  /* eslint-disable-next-line no-unused-vars */
  setTopCountriesCount: (value: number | undefined) => void;
  /* eslint-disable-next-line no-unused-vars */
  setTopCitiesCount: (value: number | undefined) => void;
  todayISO: string;
}

const MAX_TOP = 10;

const parseTrendDateMs = (value: string) => {
  const isoMatch = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

const FacebookFollowersReport: FC<FacebookFollowersReportProps> = ({
  dateFilters,
  topCountriesCount,
  topCitiesCount,
  setTopCountriesCount,
  setTopCitiesCount,
  todayISO,
}) => {
  const { t, i18n } = useTranslation();
  const apiLang = normalizeApiLang(i18n.language);
  const [trendGranularity, setTrendGranularity] =
    useState<FollowersTrendGranularity>("daily");
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const {
    kpis,
    countries,
    cities,
    followerTrend,
    loading,
    error,
  } = useFacebookFollowersReport(
    appliedStartDate,
    appliedEndDate,
    apiLang,
    trendGranularity,
  );

  const fbFollowersNarrativePayload = useMemo<Record<string, unknown>>(
    () => ({
      kpis,
      countries,
      cities,
      follower_trend: { daily: followerTrend, monthly: [] },
    }),
    [kpis, countries, cities, followerTrend],
  );

  const { narrative: fbFollowersNarrative, loading: fbFollowersNarrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "organic_media",
    tabKey: "facebook_followers_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: fbFollowersNarrativePayload,
    promptVersion: "v6",
    enabled: !loading && !error && Boolean(kpis.length),
  });

  const countriesSliderMax = MAX_TOP;
  const citiesSliderMax = MAX_TOP;
  const defaultCountriesCount = Math.max(
    1,
    Math.min(countries.length || 1, MAX_TOP)
  );
  const defaultCitiesCount = Math.max(1, Math.min(cities.length || 1, MAX_TOP));
  const countriesSliderValue = Math.min(
    countriesSliderMax,
    Math.max(1, topCountriesCount ?? defaultCountriesCount),
  );
  const citiesSliderValue = Math.min(
    citiesSliderMax,
    Math.max(1, topCitiesCount ?? defaultCitiesCount),
  );

  const trendChartData = useMemo(() => {
    const sortedTrend = [...followerTrend].sort((left, right) => {
      const leftMs = parseTrendDateMs(left.date);
      const rightMs = parseTrendDateMs(right.date);
      if (Number.isNaN(leftMs) || Number.isNaN(rightMs)) {
        return left.date.localeCompare(right.date);
      }
      return leftMs - rightMs;
    });

    if (sortedTrend.length === 0) {
      return [];
    }

    return sortedTrend.map(point => ({
      date: point.date,
      value: Number(point.value ?? 0),
      valueFormattedValue: point.valueFormattedValue,
    }));
  }, [followerTrend]);

  return (
    <div ref={pdfContainerRef}>
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t("organicMedia.tabs.facebookFollowers")}
        subtitle={t("organicMedia.followersReport.facebookPageSubtitle")}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={draftStartDate}
                endDate={draftEndDate}
                maxDate={todayISO}
                onRangeChange={onApplyDates}
              />
            </div>
            <DashboardExportButton
              dashboardName="Facebook Followers"
              dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
            />
          </>
        }
      />

      {error && (
        <div className="organic-tabs-dashboard__error" role="alert">
          {error}
        </div>
      )}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={fbFollowersNarrative}
          loading={loading || fbFollowersNarrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      {loading ? (
        <LoadingSpinner compact />
      ) : (
        <div className="organic-facebook-followers-layout">
          <div className="organic-facebook-followers-layout__top" data-pdf-block="followers-overview">
            <FacebookFollowersKpiCards kpis={kpis} />
            <FacebookNewFollowers
              trendChartData={trendChartData}
              trendGranularity={trendGranularity}
              onTrendGranularityChange={setTrendGranularity}
            />
          </div>

          <div className="organic-facebook-followers-layout__geo" data-pdf-block="followers-geo">
            <FacebookFollowersByCountry
              rows={countries}
              value={countriesSliderValue}
              max={countriesSliderMax}
              onChange={setTopCountriesCount}
            />
            <FacebookFollowersByCity
              rows={cities}
              value={citiesSliderValue}
              max={citiesSliderMax}
              onChange={setTopCitiesCount}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookFollowersReport;
